import React, { useContext } from "react";
import { FaEdit, FaClock, FaCalendarAlt, FaVideo } from "react-icons/fa";
import { HiOutlineLink } from "react-icons/hi";
import Context from "../../../../Context/Context";
import formatTime from "../../../../common/utils/format-time";

const PreviousClassTable = ({
  classes,
  userType,
  instructorList,
  getInstructor,
  onAction,
  onUpdateRecording,
  isDarkMode = true,
}) => {
  const { loadingPrevious } = useContext(Context);

  const formatDateDisplay = (epochTime) => {
    if (!epochTime) return { day: "--", month: "---" };
    try {
      const timestamp = typeof epochTime === 'string' && /^\d+$/.test(epochTime)
        ? parseInt(epochTime)
        : epochTime;
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return { day: "--", month: "---" };

      const day = date.getDate();
      const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
      const month = months[date.getMonth()];
      return { day, month };
    } catch (error) {
      return { day: "--", month: "---" };
    }
  };

  const getDayOfWeek = (epochTime) => {
    if (!epochTime) return "N/A";
    try {
      const timestamp = typeof epochTime === 'string' && /^\d+$/.test(epochTime)
        ? parseInt(epochTime)
        : epochTime;
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "N/A";

      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      return days[date.getDay()];
    } catch (error) {
      return "N/A";
    }
  };

  const getInstructorAvatar = (instructorName) => {
    const instructor = getInstructor(instructorName);
    if (instructor?.imgUrl) {
      return instructor.imgUrl;
    }
    return null;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name?.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0])?.toUpperCase();
    }
    return name?.charAt(0)?.toUpperCase();
  };

  if (loadingPrevious) {
    return (
      <div className="w-full">
        <div className="animate-pulse space-y-4">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className={`h-20 rounded-lg ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}></div>
          ))}
        </div>
      </div>
    );
  }

  if (classes.length === 0) {
    return (
      <div className="w-full py-12 text-center">
        <p className={`text-lg ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>No previous classes found yet.</p>
      </div>
    );
  }

  return (
    <div className="w-full" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Desktop Table Header - Hidden on mobile */}
      <div className={`hidden md:grid grid-cols-12 gap-4 px-4 py-3 border-b mb-2 ${isDarkMode ? 'border-[#2a2a2a]' : 'border-gray-200'}`}>
        <div className="col-span-4">
          <span className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>CLASS DETAILS</span>
        </div>
        <div className="col-span-2">
          <span className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>TIME</span>
        </div>
        <div className="col-span-2">
          <span className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>INSTRUCTOR</span>
        </div>
        <div className="col-span-2">
          <span className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>RECORDING</span>
        </div>
        <div className="col-span-2 flex justify-end">
          <span className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>ACTIONS</span>
        </div>
      </div>

      {/* Table Body */}
      <div className="space-y-2 md:space-y-2">
        {classes.map((clas) => {
          const dateDisplay = formatDateDisplay(clas.date);
          const time = formatTime(clas.date);
          const dayOfWeek = getDayOfWeek(clas.date);
          const instructorAvatar = getInstructorAvatar(clas.instructorNames);

          return (
            <div
              key={clas.classId}
              className={`md:grid md:grid-cols-12 md:gap-4 px-3 md:px-4 py-2.5 md:py-4 rounded-lg transition-colors ${isDarkMode
                ? 'bg-[#1a1a1a] hover:bg-[#222222]'
                : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                }`}
            >
              {/* Mobile Card Layout */}
              <div className="md:hidden flex flex-col gap-2">
                {/* Top Row: Date and Class Type */}
                <div className="flex items-start gap-2">
                  {/* Date Display */}
                  <div className={`flex flex-col items-center justify-center min-w-[50px] ${isDarkMode ? 'text-white' : 'text-black'
                    }`}>
                    <span className={`text-xl font-bold leading-none ${isDarkMode ? 'text-white' : 'text-black'}`}>{dateDisplay.day}</span>
                    <span className={`text-[10px] font-medium ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>{dateDisplay.month}</span>
                  </div>
                  <div className="w-[1px] h-10 bg-gray-300"></div>
                  {/* Class Info */}
                  <div className="flex flex-col justify-center flex-1 min-w-0">
                    <span className={`font-bold text-xs leading-tight ${isDarkMode ? 'text-white' : 'text-black'}`}>{clas.classType || "Class Name"}</span>
                  </div>
                </div>

                {/* Info Rows - Compact Grid */}
                <div className="grid grid-cols-2 gap-1.5 pl-[58px]">
                  {/* Time */}
                  <div className="flex items-center gap-1.5">
                    <FaClock className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{time}</span>
                  </div>

                  {/* Instructor */}
                  <div className="flex items-center gap-1.5">
                    {instructorAvatar ? (
                      <img
                        src={instructorAvatar}
                        alt={clas.instructorNames}
                        className="w-5 h-5 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-[#FDCF08] flex items-center justify-center">
                        <span className="text-black text-[9px] font-semibold">
                          {getInitials(clas?.instructorNames)}
                        </span>
                      </div>
                    )}
                    <span className={`text-xs truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{clas.instructorNames?.split(" ")[0] || "N/A"}</span>
                  </div>

                  {/* Recording Link */}
                  <div className="flex items-center gap-1.5 col-span-2">
                    <FaVideo className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    {clas.recordingLink ? (
                      <a
                        href={clas.recordingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-xs ${isDarkMode ? 'text-[#FDCF08] hover:text-[#f0c000]' : 'text-blue-600 hover:text-blue-700'} underline truncate`}
                      >
                        View Recording
                      </a>
                    ) : userType === "admin" || userType === "instructor" ? (
                      <button
                        onClick={() => onUpdateRecording(clas.classId, clas.recordingLink || "")}
                        className={`text-xs font-semibold ${isDarkMode ? 'text-[#FDCF08] hover:text-[#f0c000]' : 'text-blue-600 hover:text-blue-700'}`}
                      >
                        + Add Recording
                      </button>
                    ) : (
                      <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No Recording</span>
                    )}
                  </div>
                </div>

                {/* Actions Row */}
                {(userType === "admin" || userType === "instructor") && (
                  <div className="flex items-center justify-end gap-2 pt-1.5 border-t border-gray-300">
                    <button
                      onClick={() => onAction(clas, "edit")}
                      className={`p-1.5 rounded transition-colors ${isDarkMode
                        ? 'hover:bg-[#2a2a2a]'
                        : 'hover:bg-gray-200'
                        }`}
                      title="Edit"
                    >
                      <FaEdit className={`text-xs ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
                    </button>
                  </div>
                )}
              </div>

              {/* Desktop Grid Layout */}
              <div className="hidden md:contents">
                {/* Class Details Column - includes date */}
                <div className="col-span-4 flex items-center gap-4">
                  {/* Date Display */}
                  <div className={`flex flex-col items-start justify-center ${isDarkMode ? 'text-white' : 'text-black'
                    }`}>
                    <span className={`text-3xl font-bold leading-none ${isDarkMode ? 'text-white' : 'text-black'}`}>{dateDisplay.day}</span>
                    <span className={`text-xs text-center font-medium ${isDarkMode ? 'text-white' : 'text-gray-600'}`}>{dateDisplay.month}</span>
                  </div>
                  <div className="w-[1px] h-full bg-gray-300"></div>
                  {/* Class Info */}
                  <div className="flex flex-col justify-center flex-1">
                    <span className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>{clas.classType || "Class Name"}</span>
                  </div>
                </div>

                {/* Time Column */}
                <div className="col-span-2 flex items-center gap-2">
                  <FaClock className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
                  <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>{time}</span>
                </div>

                {/* Instructor Column */}
                <div className="col-span-2 flex items-center gap-2">
                  {instructorAvatar ? (
                    <img
                      src={instructorAvatar}
                      alt={clas.instructorNames}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#FDCF08] flex items-center justify-center">
                      <span className="text-black text-xs font-semibold">
                        {getInitials(clas?.instructorNames)}
                      </span>
                    </div>
                  )}
                  <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>{clas.instructorNames?.split(" ")[0] || "N/A"}</span>
                </div>

                {/* Recording Column */}
                <div className="col-span-2 flex items-center gap-2">
                  {clas.recordingLink ? (
                    <a
                      href={clas.recordingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`px-3 py-1.5 font-semibold text-xs rounded-lg transition-colors flex items-center gap-2 ${isDarkMode
                        ? 'bg-[#2a2a2a] text-[#FDCF08] hover:bg-[#333333]'
                        : 'bg-gray-200 text-blue-600 hover:bg-gray-300'
                        }`}
                    >
                      <FaVideo size={12} />
                      View
                    </a>
                  ) : userType === "admin" || userType === "instructor" ? (
                    <button
                      onClick={() => onUpdateRecording(clas.classId, "")}
                      className={`px-3 py-1.5 font-semibold text-xs rounded-lg transition-colors flex items-center gap-2 ${isDarkMode
                        ? 'bg-[#FDCF08]/20 text-[#FDCF08] hover:bg-[#FDCF08]/30'
                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                        }`}
                    >
                      <HiOutlineLink size={12} />
                      + Add Recording
                    </button>
                  ) : (
                    <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No Recording</span>
                  )}
                </div>

                {/* Actions Column - at the end */}
                {(userType === "admin" || userType === "instructor") && (
                  <div className="col-span-2 flex items-center justify-end gap-3">
                    <button
                      onClick={() => onAction(clas, "edit")}
                      className={`p-2 rounded transition-colors ${isDarkMode
                        ? 'hover:bg-[#2a2a2a]'
                        : 'hover:bg-gray-200'
                        }`}
                      title="Edit"
                    >
                      <FaEdit className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-700'}`} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PreviousClassTable;
